/**
 * Zustand Bound Store
 * 
 * The main store that combines all slices with middleware.
 * Features:
 * - Auto-discovered slices from feature modules
 * - Redux DevTools integration (development only)
 * - Selective localStorage persistence
 * - Auto-generated selectors
 * - Store reset for logout
 * 
 * @example
 * // Using auto-generated selectors
 * const theme = useBoundStore.use.theme();
 * const toggleSidebar = useBoundStore.use.toggleSidebar(); // from sidebarSlice
 *
 * // Using hook with custom selector
 * const { theme, sidebarState, isOnline } = useStoreShallow((s) => ({
 *   theme: s.theme,
 *   sidebarState: s.sidebarState,
 *   isOnline: s.isOnline,
 * }));
 */

import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { environment } from '@/config/environment';
import { createSelectors } from './createSelectors';
import { createShallowHook, createValueHook, createActionsHook } from './hooks';
import {
  createUISlice,
  createNetworkSlice,
  createHeaderSlice,
  createSidebarSlice,
  createAuthSlice,
  createLanguageSlice,
  createAppConfigSlice,
  uiSliceInitialState,
  networkSliceInitialState,
  headerSliceInitialState,
  sidebarSliceInitialState,
  authSliceInitialState,
  languageSliceInitialState,
  appConfigSliceInitialState,
} from './slices';
import type { BoundStoreState } from './types';

// ============================================================================
// Initial State (for reset)
// ============================================================================

const initialState = {
  ...uiSliceInitialState,
  ...networkSliceInitialState,
  ...headerSliceInitialState,
  ...sidebarSliceInitialState,
  ...authSliceInitialState,
  ...languageSliceInitialState,
  ...appConfigSliceInitialState,
};

// ============================================================================
// Store Creation
// ============================================================================

/**
 * The raw bound store without auto-selectors.
 * Use useBoundStore (exported below) which includes auto-generated selectors.
 */
const useBoundStoreBase = create<BoundStoreState>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get, store) => ({
          // Built-in slices
          ...createUISlice(set, get, store),
          ...createNetworkSlice(set, get, store),
          ...createHeaderSlice(set, get, store),
          ...createSidebarSlice(set, get, store),
          ...createAuthSlice(set, get, store),
          ...createLanguageSlice(set, get, store),
          ...createAppConfigSlice(set, get, store),

          // Reset action - call this on logout (preserve appConfig - deployment-level, not user-specific)
          resetStore: () =>
            set(
              (state) => ({
                ...(initialState as BoundStoreState),
                countryId: state.countryId,
                clientId: state.clientId,
                countryName: state.countryName,
                clientName: state.clientName,
                defaultPhoneCountry: state.defaultPhoneCountry,
                allowedPhoneCountries: state.allowedPhoneCountries,
                appConfigReady: state.appConfigReady,
              }),
              false,
              'store/reset'
            ),
        }),
        {
          name: 'bls-booking-portal-store',
          // Persist UI preferences only
          partialize: (state) => ({
            theme: state.theme,
            sidebarState: state.sidebarState,
            currentLanguage: state.currentLanguage,
          }),
        }
      )
    ),
    {
      name: 'BLS Booking Portal Store',
      enabled: environment.isNonProduction,
    }
  )
);

// ============================================================================
// Store with Auto-Generated Selectors
// ============================================================================

/**
 * The main store hook with auto-generated selectors.
 * 
 * @example
 * // Auto-generated selector (recommended for single values)
 * const theme = useBoundStore.use.theme();
 * 
 * // Custom selector (for derived/computed values)
 * const isDarkMode = useBoundStore((s) => s.theme === 'dark');
 * 
 * // Multiple values with shallow comparison
 * const { theme, isOnline } = useStoreShallow((s) => ({
 *   theme: s.theme,
 *   isOnline: s.isOnline,
 * }));
 */
export const useBoundStore = createSelectors(useBoundStoreBase);

// ============================================================================
// Optimized Hooks (using hook factories)
// ============================================================================

/**
 * Hook for selecting multiple properties with automatic shallow comparison.
 * Prevents re-renders when other unrelated state changes.
 */
export const useStoreShallow = createShallowHook(useBoundStoreBase);

/**
 * Hook for selecting a single primitive value.
 * For single values, this is more efficient than useShallow.
 */
export const useStoreValue = createValueHook(useBoundStoreBase);

/**
 * Hook for selecting multiple actions.
 * Actions are stable references and don't cause re-renders.
 */
export const useStoreActions = createActionsHook(useBoundStoreBase);

// ============================================================================
// Non-React Access (for sagas, services, etc.)
// ============================================================================

/**
 * Get current state outside of React components.
 * Useful for sagas, API interceptors, or other non-React code.
 * 
 * @example
 * // In a saga or service
 * const currentTheme = getStoreState().theme;
 */
export const getStoreState = useBoundStoreBase.getState;

/**
 * Set state outside of React components.
 * Use sparingly - prefer dispatching from components.
 * 
 * @example
 * // In an event handler or saga
 * setStoreState({ isOnline: navigator.onLine });
 */
export const setStoreState = useBoundStoreBase.setState;

/**
 * Subscribe to state changes outside of React.
 * Returns an unsubscribe function.
 * 
 * @example
 * const unsubscribe = subscribeToStore(
 *   (state) => state.isOnline,
 *   (isOnline) => console.log('Online status:', isOnline)
 * );
 */
export const subscribeToStore = useBoundStoreBase.subscribe;

// ============================================================================
// Re-exports
// ============================================================================

export * from './types';
export {
  createShallowHook,
  createValueHook,
  createActionsHook,
  // Header hooks
  useHeaderActionButtons,
  useHeaderBackButton,
  useHeaderState,
  // Sidebar hooks
  useSidebarStore,
  useSidebarState,
  useSidebarToggle,
  useIsMobile,
  SidebarResponsiveHandler,
} from './hooks';

